import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, GraduationCap, Award, Users } from 'lucide-react';
import SchoolSettingsContext from '../context/SchoolSettingsContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Logo URL
const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_a05533bd-d761-4a3f-beb7-e7e62bdd4f88/artifacts/glvqb9sk_ChatGPT%20Image%20Aug%2013%2C%202026%20at%2002_26_31%20PM.png";

// Default school settings
const defaultSettings = {
  school_name: 'Merit Plus',
  school_subtitle: 'Мектеп жетістіктерін басқару жүйесі',
  logo_url: LOGO_URL,
};

export default function SchoolLogin() {
  const navigate = useNavigate();
  const { subdomain } = useParams();
  const { settings: schoolSettings, loading: settingsLoading, setSchoolFromPath } = useContext(SchoolSettingsContext);
  const { login } = useAuth();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localSettings, setLocalSettings] = useState(defaultSettings);
  const [settingsError, setSettingsError] = useState(false);

  // Trigger school settings fetch when path changes
  useEffect(() => {
    if (subdomain) {
      setSchoolFromPath(subdomain);
    }
  }, [subdomain, setSchoolFromPath]);

  useEffect(() => {
    if (schoolSettings && schoolSettings.id) {
      const name = schoolSettings.school_name || schoolSettings.name || defaultSettings.school_name;
      const subtitle = schoolSettings.school_subtitle || schoolSettings.subtitle || defaultSettings.school_subtitle;
      
      setLocalSettings({
        ...defaultSettings,
        ...schoolSettings,
        school_name: name,
        school_subtitle: subtitle,
        logo_url: schoolSettings.logo_url || LOGO_URL,
      });
      setSettingsError(false);
    } else if (!settingsLoading && subdomain) {
      setSettingsError(true);
    }
  }, [schoolSettings, settingsLoading, subdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error('Пайдаланушы атын және құпия сөзді енгізіңіз');
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(username.trim(), password, subdomain);
      
      toast.success(`Қош келдіңіз, ${user.full_name || user.username}!`);
      localStorage.setItem('school_subdomain', subdomain);
      
      const basePath = `/${subdomain}`;
      switch (user.role) {
        case 'admin':
          navigate(`${basePath}/admin`);
          break;
        case 'teacher':
          navigate(`${basePath}/teacher`);
          break;
        case 'student':
          navigate(`${basePath}/student`);
          break;
        case 'super_admin':
          navigate('/superadmin');
          break;
        default:
          navigate(basePath);
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Кіру кезінде қате орын алды';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Жүктелуде...</p>
        </div>
      </div>
    );
  }

  if (settingsError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-slate-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Мектеп табылмады</h2>
          <p className="text-slate-600 mb-6">Сілтеме дұрыс емес немесе мектеп тіркелмеген.</p>
          <Button 
            onClick={() => navigate('/')} 
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="back-to-home-btn"
          >
            Басты бетке оралу
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      <div className="min-h-screen flex">
        {/* Left Side - School Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-teal-600 to-teal-700 p-12 flex-col justify-between relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            <img 
              src={localSettings.logo_url} 
              alt="Logo" 
              className="w-20 h-20 rounded-xl shadow-lg mb-8 bg-white p-2"
            />
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="school-name-display">
              {localSettings.school_name}
            </h1>
            <p className="text-teal-100 text-xl" data-testid="school-subtitle-display">
              {localSettings.school_subtitle}
            </p>
          </div>

          {/* Features List */}
          <div className="relative z-10 space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Мақтау & Сөгіс жүйесі</h3>
                <p className="text-teal-100 text-sm">Оқушылардың жетістіктерін бақылаңыз</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Сынып талдауы</h3>
                <p className="text-teal-100 text-sm">Толық статистика мен есептер</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Оқушы порталы</h3>
                <p className="text-teal-100 text-sm">Нәтижелерді қадағалаңыз</p>
              </div>
            </div>
          </div>

          <div className="relative z-10">
            <p className="text-teal-200 text-sm">
              © 2026 Merit Plus. Барлық құқықтар қорғалған.
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <img 
                src={localSettings.logo_url} 
                alt="Logo" 
                className="w-16 h-16 rounded-xl shadow-lg mx-auto mb-4 bg-white p-2"
              />
              <h1 className="text-2xl font-bold text-slate-900" data-testid="mobile-school-name">
                {localSettings.school_name}
              </h1>
              <p className="text-slate-600">{localSettings.school_subtitle}</p>
            </div>

            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Жүйеге кіру
                </h2>
                <p className="text-slate-500">
                  Есептік жазбаңызбен кіріңіз
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-700 font-medium">
                    Пайдаланушы аты
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Логин"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                    className="h-12 bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900 placeholder:text-slate-400"
                    data-testid="username-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">
                    Құпия сөз
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      className="h-12 bg-slate-50 border-slate-200 focus:border-teal-500 focus:ring-teal-500 text-slate-900 placeholder:text-slate-400 pr-12"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-lg shadow-teal-600/25 transition-all duration-200"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Кіру...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-5 h-5" />
                      Кіру
                    </span>
                  )}
                </Button>
              </form>

              {/* Help text */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <p className="text-center text-sm text-slate-500">
                  Құпия сөзді ұмыттыңыз ба?{' '}
                  <span className="text-teal-600 hover:text-teal-700 cursor-pointer font-medium">
                    Әкімшіге хабарласыңыз
                  </span>
                </p>
              </div>
            </div>

            {/* Back button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
                data-testid="back-to-landing-btn"
              >
                ← Басты бетке оралу
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
